import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  Address,
  AddressValidation,
  BoelsBillingAddress,
  Cart,
  DeliveryMode,
  DeliveryModeResponse,
} from '@boels/models';
import { concatMap, forkJoin, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { DeliveryGateway } from '../gateway/delivery-gateway.service';
import { CartState } from '../../cart/reducers/cart.reducer';
import { Store } from '@ngrx/store';
import { loadCart } from '../../cart/actions/cart.action';

@Injectable({
  providedIn: 'root',
})
export class DeliveryFacade {
  #store: Store<CartState> = inject(Store);
  #deliveryModeGateway: DeliveryGateway = inject(DeliveryGateway);

  getDeliveryModes(): Observable<DeliveryMode[]> {
    return this.#deliveryModeGateway
      .getDeliveryModeResponse()
      .pipe(map((mode: DeliveryModeResponse) => mode.deliveryModes));
  }

  setDeliveryMode(
    deliveryMode: DeliveryMode,
  ): Observable<HttpResponse<DeliveryMode>> {
    return this.#deliveryModeGateway.setDeliveryMode(deliveryMode);
  }

  verifyAddress(address: Address): Observable<AddressValidation> {
    return this.#deliveryModeGateway.verifyAddress(address);
  }

  setShippingAddress(
    address: BoelsBillingAddress,
    previousOperationalDirections?: string,
  ): Observable<Address> {
    return this.#deliveryModeGateway.createDeliveryAddress(address).pipe(
      concatMap((createdAddress) => {
        const operations = [
          this.#deliveryModeGateway
            .setDeliveryAddress(createdAddress?.id as string)
            .pipe(
              map(() => createdAddress),
              take(1),
            ),
        ];

        // Check if the current operational directions are different from the previous ones
        // If so, check if the previous operational directions is undefined then we know no previous operational directions existed then we do a post
        // Else if the current operational directions is empty then we delete the operational directions
        // Else we update the operational directions
        if (address?.operationalDirections !== previousOperationalDirections) {
          const method: 'DELETE' | 'PUT' =
            address.operationalDirections === '' ? 'DELETE' : 'PUT';

          operations.push(
            this.#deliveryModeGateway
              .setOperationalDirections(address?.operationalDirections, method)
              .pipe(
                tap(() => this.#store.dispatch(loadCart())),
                map((res: Cart) => res?.deliveryAddress || address),
                take(1),
              ),
          );
        }
        return forkJoin(operations).pipe(
          map((results) =>
            results.reduce((acc, curr) => ({ ...acc, ...curr }), {}),
          ),
        );
      }),
    );
  }
}
